import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { PartInfo } from "../../../../shared/models";
import { STRAND_CONTAINER_IDS } from "../../models";
import type { PartsTreeRowAPI } from "../../models";
import {
  setMoldOnline,
  setMoldOffline,
  installMoldPart,
  installExistingMoldPart,
  uninstallMoldPart,
  repairMoldPart,
  upsertMoldPartDetails,
  createSparePart,
  deleteSparePart,
} from "../../repositories/CasterMoldRepository";
// =============================================================================
// Targeted Invalidation Helpers
//
// Caster splits the tree into 5 strand caches + 1 offline cache.
// Instead of invalidating ALL 6 tree queries on every mutation,
// we only invalidate the caches that the operation actually affects.
// =============================================================================

/**
 * Look up which strand a part belongs to by checking the cached tree data.
 * Returns the strandId if found, null if the part is offline/not cached.
 * Runs client-side only — no API call.
 */
const findStrandForPart = (qc: QueryClient, partId: number): number | null => {
  for (const strandId of STRAND_CONTAINER_IDS) {
    const data = qc.getQueryData<PartsTreeRowAPI[]>([
      "caster-mold-strand",
      strandId,
    ]);
    if (
      data?.some((row) => row.L2PartId === partId || row.L1PartId === partId)
    ) {
      return strandId;
    }
  }
  return null;
};

/** Invalidate a specific strand's tree cache */
const invalidateStrand = (qc: QueryClient, strandId: number) =>
  qc.invalidateQueries({ queryKey: ["caster-mold-strand", strandId] });

/** Invalidate the offline tree cache */
const invalidateOffline = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: ["caster-mold-offline"] });

/** Invalidate all part-details caches */
const invalidateDetails = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: ["caster-mold-details"] });

/** Invalidate all strand-history caches */
const invalidateStrandHistory = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: ["caster-mold-strand-history"] });

/**
 * Invalidate the strand that contains a part + offline + details + strand history.
 * If not found in any strand cache, the part is offline — only offline + details.
 */
const invalidateForPart = (qc: QueryClient, partId: number) => {
  const strandId = findStrandForPart(qc, partId);
  if (strandId) {
    invalidateStrand(qc, strandId);
  }
  invalidateOffline(qc);
  invalidateDetails(qc);
  invalidateStrandHistory(qc);
};

// =============================================================================
// Set Online / Set Offline
// =============================================================================

interface SetMoldOnlineParams {
  moldPartId: number;
  strandPartId: number;
  timestamp: Date | null;
}

export const useSetMoldOnlineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: SetMoldOnlineParams) => {
      const resp = await setMoldOnline(
        params.moldPartId,
        params.strandPartId,
        undefined,
        undefined,
        params.timestamp,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to set mold online");
      return resp;
    },
    onSuccess: (_data, variables) => {
      // Mold moves from offline → target strand
      invalidateStrand(queryClient, variables.strandPartId);
      invalidateOffline(queryClient);
      invalidateDetails(queryClient);
      invalidateStrandHistory(queryClient);
    },
  });
};

interface SetMoldOfflineParams {
  moldPartId: number;
  timestamp: Date | null;
}

export const useSetMoldOfflineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: SetMoldOfflineParams) => {
      const resp = await setMoldOffline(
        params.moldPartId,
        undefined,
        undefined,
        params.timestamp,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to set mold offline");
      return resp;
    },
    onSuccess: (_data, variables) => {
      // Mold moves from strand → offline
      invalidateForPart(queryClient, variables.moldPartId);
    },
  });
};

// =============================================================================
// Install Part (new + existing)
// =============================================================================

interface InstallMoldPartParams {
  moldPartId: number;
  positionId: number;
  partTypeId: number;
  partInfo: PartInfo;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useInstallMoldPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: InstallMoldPartParams) => {
      const resp = await installMoldPart(
        params.moldPartId,
        params.positionId,
        params.partTypeId,
        params.partInfo,
        params.comments,
        params.operatorNumber,
        undefined,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to install part");
      return resp;
    },
    onSuccess: (_data, variables) => {
      // New part installed on mold — affects mold's strand + offline (spares change)
      invalidateForPart(queryClient, variables.moldPartId);
    },
  });
};

interface InstallExistingPartParams {
  existingPartId: number;
  moldPartId: number;
  positionId: number;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useInstallExistingPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: InstallExistingPartParams) => {
      const resp = await installExistingMoldPart(
        params.existingPartId,
        params.moldPartId,
        params.positionId,
        params.comments,
        params.operatorNumber,
        undefined,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to install part");
      return resp;
    },
    onSuccess: (_data, variables) => {
      // Part moves from spares → mold's strand
      invalidateForPart(queryClient, variables.moldPartId);
    },
  });
};

// =============================================================================
// Uninstall / Repair
// =============================================================================

interface UninstallPartParams {
  partId: number;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useUninstallMoldPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UninstallPartParams) => {
      const resp = await uninstallMoldPart(
        params.partId,
        params.operatorNumber,
        params.comments,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to uninstall part");
      return resp;
    },
    onSuccess: (_data, variables) => {
      // Part moves from strand → spares (offline)
      invalidateForPart(queryClient, variables.partId);
    },
  });
};

interface RepairPartParams {
  partId: number;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useRepairMoldPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: RepairPartParams) => {
      const resp = await repairMoldPart(
        params.partId,
        params.operatorNumber,
        params.comments,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to reset part");
      return resp;
    },
    onSuccess: (_data, variables) => {
      // Metrics change — only affects the part's location
      invalidateForPart(queryClient, variables.partId);
    },
  });
};

// =============================================================================
// Edit Part Details
// =============================================================================

interface UpsertPartDetailsParams {
  partId: number;
  partInfo: PartInfo;
  comments: string | null;
}

export const useUpsertMoldPartDetailsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UpsertPartDetailsParams) => {
      const resp = await upsertMoldPartDetails(
        params.partId,
        params.partInfo,
        params.comments,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to update part details");
      return resp;
    },
    onSuccess: () => {
      // Details only — no tree structure change
      invalidateDetails(queryClient);
    },
  });
};

// =============================================================================
// Spare Parts (create / delete)
// =============================================================================

interface CreateSparePartParams {
  containerId: number;
  partInfo: PartInfo;
  userId: number | null;
}

export const useCreateSparePartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateSparePartParams) => {
      const resp = await createSparePart(
        params.containerId,
        params.partInfo,
        params.userId,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to create spare part");
      return resp;
    },
    onSuccess: () => {
      // New spare part — only offline tree changes
      invalidateOffline(queryClient);
      invalidateDetails(queryClient);
    },
  });
};

interface DeleteSparePartParams {
  partId: number;
  userId: number | null;
}

export const useDeleteSparePartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: DeleteSparePartParams) => {
      const resp = await deleteSparePart(params.partId, params.userId);
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to delete spare part");
      return resp;
    },
    onSuccess: () => {
      // Spare removed — only offline tree changes
      invalidateOffline(queryClient);
      invalidateDetails(queryClient);
    },
  });
};
